import { useCallback, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';

/**
 * Mouse-parallax helper for a hero element. Attach `onMouseMove`/`onMouseLeave`
 * to the hero container; read `offset` to translate a decorative layer (e.g. a
 * glow blob) by a fraction of the cursor's distance from center. No-ops under
 * reduced motion.
 *
 * `strength` is the max px travel at the container edge.
 */
export function useParallax(strength = 18) {
  const reduced = useReducedMotion();
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const raf = useRef<number | null>(null);

  const onMouseMove = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (reduced) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width - 0.5; // -0.5..0.5
      const py = (e.clientY - rect.top) / rect.height - 0.5;
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(() =>
        setOffset({ x: px * strength * 2, y: py * strength * 2 }),
      );
    },
    [reduced, strength],
  );

  const onMouseLeave = useCallback(() => {
    if (reduced) return;
    setOffset({ x: 0, y: 0 });
  }, [reduced]);

  return { offset, onMouseMove, onMouseLeave, reduced };
}
